"use client";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";

const MILESTONES = [25, 50, 75, 100] as const;

export function ScrollDepthTracker() {
  const pathname = usePathname();
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    fired.current = new Set();
  }, [pathname]);

  useEffect(() => {
    let ticking = false;
    const check = () => {
      ticking = false;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) {
        // Short page — credit 100% immediately the first time we observe it.
        if (!fired.current.has(100)) {
          fired.current.add(100);
          pushEvent(analyticsEvents.scroll_depth, {
            milestone: 100,
            path: pathname ?? location.pathname,
          });
        }
        return;
      }
      const pct = (window.scrollY / max) * 100;
      for (const m of MILESTONES) {
        if (pct >= m && !fired.current.has(m)) {
          fired.current.add(m);
          pushEvent(analyticsEvents.scroll_depth, {
            milestone: m,
            path: pathname ?? location.pathname,
          });
        }
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    check();
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return null;
}
